/**
 * WeeklyTrendChart — Bar chart showing total sales per week
 * for the past 24 weeks. Uses Recharts BarChart.
 */

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
} from "recharts";
import { WeeklyTrendPoint } from "../utils/PowerBIApiCall";
import {
  formatCurrencyAxis as formatYAxis,
  formatCurrency as formatTooltipValue,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface WeeklyTrendChartProps {
  data: WeeklyTrendPoint[];
}

/** Custom tooltip styled to match the app's card aesthetic */
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">
        Week of {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.name} className="text-xs" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}:</span>{" "}
          <span className="tabular-nums">
            {formatTooltipValue(entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

export function WeeklyTrendChart({ data }: WeeklyTrendChartProps) {
  if (!data || data.length === 0) {
    return null;
  }

  // Calculate average for reference line
  const avgSales = data.reduce((sum, d) => sum + d.totalSales, 0) / data.length;

  // Only show PY bars when the data actually has PY values
  const hasPY = data.some((d) => d.pyTotalSales > 0);

  return (
    <div className="card overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Weekly Sales Trend
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">Past {data.length} weeks</p>
      </div>
      <div className="px-4 py-5">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis
              dataKey="weekStartFormatted"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              interval={1}
              angle={-45}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={65}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Average reference line */}
            <ReferenceLine
              y={avgSales}
              stroke={SERIES.priorYearLine}
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{
                value: `Avg: ${formatYAxis(avgSales)}`,
                position: "right",
                fontSize: 10,
                fill: SERIES.priorYearLine,
              }}
            />
            {hasPY && (
              <Legend
                iconType="square"
                wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
              />
            )}
            {/* PY Total Sales bars — only when data has PY values */}
            {hasPY && (
              <Bar
                dataKey="pyTotalSales"
                name="PY Total Sales"
                fill={SERIES.priorYear}
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
              />
            )}
            {/* Total Sales bars — primary blue */}
            <Bar
              dataKey="totalSales"
              name="Total Sales"
              fill={SERIES.primary}
              radius={[3, 3, 0, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
